import { useEffect, useState } from 'react'
import {
  Button,
  FlexCell,
  FlexRow,
  FlexSpacer,
  LabeledInput,
  PickerInput,
  Rating,
  TextArea,
  useForm,
} from '@epam/loveship'
import { useArrayDataSource, useUuiContext, UuiContexts } from '@epam/uui-core'
import { amplTrack } from '@stlm/common-ui/lib'
import { useGetLayersQuery, usePostPointMutation } from '../../../../store'
import { SidebarPanelHeader } from '../../../../components/SidebarPanel/components/SidebarPanelHeader'
import { SidebarPanelMain } from '../../../../components/SidebarPanel/components/SidebarPanelMain'
import {
  LayerResponse,
  NewPoint,
  PointResponse,
} from '../../../../constants/types'
import { MAX_CHAR_LIMIT } from '../../../../constants/values'
import { useActions } from '../../../../hooks/redux/actions'
import { useAppSelector } from '../../../../hooks/redux/redux'
import { useSideBarControlls } from '../../../../hooks/useSideBarControlls'
import { useFetchLocationAddress } from '../../../../hooks/useFetchLocationAddress'
import { showErrorNotification } from '../../../../utils/notifications/showErrorNotification'
import { showSuccessNotification } from '../../../../utils/notifications/showSuccessNotification'
import { showLeavePointCreationModal } from '../../../../utils/modals/showLeavePointCreationModal'
import { showConfirmPlaceDuplicateModal } from '../../../../utils/modals/showConfirmPointDuplicateModal'

import css from '../NewPointForm/NewPointForm.module.scss'

export const NewPointForm = () => {
  const { data: layers = [] } = useGetLayersQuery()
  const { newPointPosition, currentPoints } = useAppSelector(
    (state) => state.points
  )
  const { isSideBarCloseInitiated } = useAppSelector((state) => state.app)
  const { closeCreateNewPoint } = useSideBarControlls()
  const {
    addCurrentPoint,
    closeNewPoint,
    setSideBarClosingInitiated,
    setNewPointPosition,
  } = useActions()

  const address = useFetchLocationAddress(newPointPosition)

  useEffect(() => {
    if (isSideBarCloseInitiated) onCloseAddNewPointForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSideBarCloseInitiated])

  const svc = useUuiContext<UuiContexts>()
  const [postPoint] = usePostPointMutation()

  const [isSubmitting, setIsSubmitting] = useState(false)

  const onCloseAddNewPointForm = () => {
    setSideBarClosingInitiated(false)
    showLeavePointCreationModal(svc).then(() => {
      closeCreateNewPoint()
      setNewPointPosition(null)
    })
  }

  const checkDuplicates = async () => {
    let duplicatePlaceCancelled = false

    if (
      currentPoints?.find(
        (point) =>
          point.location === address.location &&
          point.name.toLowerCase() === pointNameValue.toLowerCase()
      )
    ) {
      await showConfirmPlaceDuplicateModal(svc).then(
        () => {},
        () => {
          duplicatePlaceCancelled = true
        }
      )
    }

    return duplicatePlaceCancelled
  }
  const { lens, save } = useForm<NewPoint>({
    value: {
      pointName: '',
      pointDescription: '',
      pointCategory: null,
      pointLocation: address.location,
      countryCode: address.countryCode,
      lat: 0,
      lng: 0,
      itemReview: '',
      itemRating: 0,
    },
    // Passed null to prevent show "Save Changes?" dialog after history.push
    // Expected behaviour with null: always saves changes when unmount
    beforeLeave: null,
    onSave: async (newPoint) => {
      const duplicatePlaceCancelled = await checkDuplicates()

      if (duplicatePlaceCancelled) return

      setIsSubmitting(true)
      const bodyNewPoint = {
        layerId: newPoint.pointCategory,
        name: newPoint.pointName,
        description: newPoint.pointDescription,
        lat: newPointPosition?.lat,
        lng: newPointPosition?.lng,
        location: address.location,
        countryCode: address.countryCode,
        rating: newPoint.itemRating,
        review: newPoint.itemReview,
      }

      amplTrack({
        name: 'map_add_place_form_submit',
        place_type: newPoint.pointCategory,
        point_name: newPoint.pointName,
      })

      const savedPoint = await postPoint(bodyNewPoint).unwrap()
      if (savedPoint) {
        addCurrentPoint(savedPoint as PointResponse)
      }
      return savedPoint
    },
    onSuccess: (result) => {
      setIsSubmitting(false)

      closeCreateNewPoint()
      closeNewPoint()
      setNewPointPosition(null)

      showSuccessNotification({ svc, message: 'Place saved' })
    },
    onError: (error) => {
      setIsSubmitting(false)

      showErrorNotification({
        svc,
        message: `Error on save: ${error.message}`,
      })
    },
    getMetadata: () => ({
      props: {
        pointName: {
          isRequired: true,
          validators: [
            (val) => [!val.length && 'Please fill in the Place Name field'],
          ],
        },
        pointCategory: {
          isRequired: true,
          validators: [
            (val) => [!val && 'Please choose a Category to proceed'],
          ],
        },
        pointDescription: {
          isRequired: true,
          validators: [
            (val) => [
              !val.length && 'Please fill in the Place Description field',
            ],
          ],
        },
        itemRating: {
          isRequired: true,
          validators: [(val) => [!val && 'Please choose a Rating to proceed']],
        },
        itemReview: { isRequired: false },
      },
    }),
  })

  const [pointDescriptionValue, setPointDescriptionValue] = useState(
    lens.prop('pointDescription').get()
  )
  const [pointNameValue, setPointNameValue] = useState(
    lens.prop('pointName').get()
  )
  const [itemReviewValue, setItemReviewValue] = useState(
    lens.prop('itemReview').get()
  )
  const [itemRating, setItemRating] = useState(lens.prop('itemRating').get())
  const [pointCategory, setPointCategory] = useState(
    lens.prop('pointCategory').get()
  )

  const onSave = () => {
    lens.prop('pointDescription').set(pointDescriptionValue)
    lens.prop('pointName').set(pointNameValue)
    lens.prop('pointCategory').set(pointCategory)
    lens.prop('itemReview').set(itemReviewValue)
    lens.prop('itemRating').set(itemRating)
    save()
  }

  const sortedLayers = [...layers].sort(
    (a, b) => a.showPriority - b.showPriority
  )
  const dataSource = useArrayDataSource(
    { items: sortedLayers || ([] as LayerResponse[]) },
    []
  )

  const isMaxLimitReached =
    lens.prop('itemReview').toProps().value.length >= MAX_CHAR_LIMIT.REVIEW
  const isMaxDescription =
    lens.prop('pointDescription').toProps().value.length >=
    MAX_CHAR_LIMIT.DESCRIPTION

  return (
    <>
      <SidebarPanelHeader title="Add New Place" />

      <SidebarPanelMain>
        <FlexRow vPadding="12" alignItems="top">
          <FlexCell grow={1}>
            <LabeledInput
              label="Place Category"
              {...lens.prop('pointCategory').toProps()}
            >
              <PickerInput
                value={pointCategory}
                onValueChange={setPointCategory}
                dataSource={dataSource}
                minBodyWidth={4}
                getRowOptions={(item) => ({
                  isDisabled: !item?.parentId,
                  isSelectable: !!item?.parentId,
                })}
                getName={(item) => item?.name || 'n/a'}
                placeholder={'Select place category'}
                selectionMode="single"
                valueType="id"
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput
              label="Place Name"
              {...lens.prop('pointName').toProps()}
            >
              <TextArea
                value={pointNameValue}
                onValueChange={setPointNameValue}
                autoSize={true}
                maxLength={MAX_CHAR_LIMIT.NAME}
                placeholder="Place Name"
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput
              label="Place Description"
              {...lens.prop('pointDescription').toProps()}
            >
              <TextArea
                value={pointDescriptionValue}
                onValueChange={setPointDescriptionValue}
                cx={isMaxDescription ? 'textAreaLimitReached' : ''}
                maxLength={MAX_CHAR_LIMIT.DESCRIPTION}
                autoSize
                rows={2}
                placeholder="Place Description"
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput
              label="Place Location"
              {...lens.prop('pointLocation').toProps()}
            >
              <TextArea
                cx={isMaxDescription ? 'textAreaLimitReached' : ''}
                maxLength={MAX_CHAR_LIMIT.DESCRIPTION}
                autoSize
                rows={2}
                placeholder="Place Location"
                {...lens.prop('pointLocation').toProps()}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell width="auto">
            <LabeledInput
              label="Place Rating"
              {...lens.prop('itemRating').toProps()}
            >
              {/*Rating field workaround for UUI issue https://github.com/epam/UUI/issues/1168*/}
              <Rating
                value={itemRating}
                onValueChange={setItemRating}
                isInvalid={false}
                from={1}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexCell grow={1}>
            <LabeledInput
              label="Place Review"
              {...lens.prop('itemReview').toProps()}
            >
              <TextArea
                value={itemReviewValue}
                onValueChange={setItemReviewValue}
                cx={isMaxLimitReached ? 'textAreaLimitReached' : ''}
                autoSize
                rows={2}
                placeholder="Place Review"
                maxLength={MAX_CHAR_LIMIT.REVIEW}
              />
            </LabeledInput>
          </FlexCell>
        </FlexRow>

        <FlexRow vPadding="12">
          <FlexSpacer />
          <Button
            caption="Cancel"
            onClick={onCloseAddNewPointForm}
            color="sky"
            fill="white"
          />
          <Button
            caption="Save"
            onClick={onSave}
            color="sky"
            cx={css.marginBtn}
            isDisabled={isSubmitting}
          />
        </FlexRow>
      </SidebarPanelMain>
    </>
  )
}
